import LocalLink from "@/components/LocalLink";
import logoWhite from "@/assets/logo-event-boutique-white.webp";
import { useLocale } from "@/lib/i18n/useLocale";
import { localise, type Locale } from "@/lib/i18n/registry";

const linkClass = "text-[14px] opacity-80 hover:opacity-100 transition-opacity";

/**
 * Футер одинаков на обоих языках (BTQ-47, Этап 1): те же четыре колонки, те же
 * ссылки, адреса — из реестра пар по локали. До версии v1 футер был написан
 * вручную ссылка за ссылкой, поэтому португальская сторона его просто не имела.
 *
 * Пока перевод страницы не готов, её пункт ведёт на английскую версию и
 * помечается data-i18n-fallback: колонка остаётся полной, тупиков нет.
 */
interface FooterLink {
  key: string;
  label: Record<Locale, string>;
  dim?: boolean;
}

interface FooterColumn {
  title: Record<Locale, string>;
  aria: Record<Locale, string>;
  links: FooterLink[];
}

const COLUMNS: FooterColumn[] = [
  {
    title: { en: "Company", pt: "Atelier" },
    aria: { en: "Company navigation", pt: "Navegação do atelier" },
    links: [
      { key: "about", label: { en: "About Us", pt: "Sobre nós" } },
      { key: "portfolio", label: { en: "Portfolio", pt: "Portfólio" } },
      { key: "venues", label: { en: "Venues", pt: "Quintas" } },
      { key: "blog", label: { en: "Blog", pt: "Blog" } },
      { key: "contact", label: { en: "Contact", pt: "Contactos" } },
    ],
  },
  {
    title: { en: "Services", pt: "Serviços" },
    aria: { en: "Services navigation", pt: "Navegação de serviços" },
    links: [
      { key: "services", label: { en: "Services Overview", pt: "Todos os serviços" } },
      { key: "service-wedding-planning", label: { en: "Wedding Planning", pt: "Organização de casamentos" } },
      { key: "service-creative-direction", label: { en: "Creative Direction", pt: "Direção criativa" } },
      { key: "service-event-production", label: { en: "Event Production", pt: "Produção de eventos" } },
      { key: "service-wedding-coordination", label: { en: "Wedding Coordination", pt: "Coordenação de casamentos" } },
      { key: "service-private-concierge", label: { en: "Private Concierge", pt: "Concierge privado" } },
    ],
  },
  {
    title: { en: "Locations", pt: "Regiões" },
    aria: { en: "Locations navigation", pt: "Navegação por região" },
    links: [
      { key: "locations", label: { en: "Locations Overview", pt: "Todas as regiões" } },
      { key: "location-lisbon", label: { en: "Lisbon", pt: "Lisboa" } },
      { key: "location-cascais", label: { en: "Cascais", pt: "Cascais" } },
      { key: "location-sintra", label: { en: "Sintra", pt: "Sintra" } },
      { key: "location-comporta", label: { en: "Comporta", pt: "Comporta" } },
      { key: "location-porto", label: { en: "Porto", pt: "Porto" } },
      { key: "location-algarve", label: { en: "Algarve", pt: "Algarve" } },
      { key: "location-douro-valley", label: { en: "Douro Valley", pt: "Douro" } },
      { key: "location-alentejo", label: { en: "Alentejo", pt: "Alentejo" } },
      { key: "location-madeira", label: { en: "Madeira", pt: "Madeira" } },
      { key: "location-hidden-gems", label: { en: "Hidden Gems", pt: "Lugares secretos" } },
    ],
  },
  {
    title: { en: "Resources & Legal", pt: "Recursos e legal" },
    aria: { en: "Resources navigation", pt: "Navegação de recursos" },
    links: [
      { key: "legal", label: { en: "Legal Requirements", pt: "Casar em Portugal: documentos" } },
      { key: "faq", label: { en: "FAQ", pt: "Perguntas frequentes" } },
      { key: "blog-palaces", label: { en: "Palace Wedding Venues", pt: "Palácios para casamentos" } },
      { key: "blog-beach", label: { en: "Beach Wedding Venues", pt: "Casamento na praia" } },
      { key: "privacy", label: { en: "Privacy Policy", pt: "Política de privacidade" } },
      { key: "terms", label: { en: "Terms & Conditions", pt: "Termos e condições" } },
      { key: "partners", label: { en: "Work with us as a supplier", pt: "Trabalhar connosco" }, dim: true },
      { key: "careers", label: { en: "Careers", pt: "Carreiras" }, dim: true },
    ],
  },
];

const RIGHTS: Record<Locale, string> = {
  en: "All rights reserved.",
  pt: "Todos os direitos reservados.",
};


const Footer = () => {
  const locale = useLocale();

  return (
  <footer className="bg-foreground text-primary-foreground" data-header-theme="dark">
    <div className="luxury-container section-luxury">
      {/* 4-Column Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
        {COLUMNS.map((col) => (
          <div key={col.title.en}>
            <p className="text-caption mb-7 opacity-80">{col.title[locale]}</p>
            <nav className="flex flex-col gap-3" aria-label={col.aria[locale]}>
              {col.links.map((l) => {
                const { href, fellBack, keepsEnglish } = localise(l.key, locale);
                // Вести некуда — пункт не рисуется, чтобы не выбрасывать
                // португальского посетителя на английскую страницу.
                if (!href) return null;
                return (
                  <LocalLink
                    key={l.key}
                    to={href}
                    hrefLang={keepsEnglish ? "en" : undefined}
                    data-i18n-substitute={fellBack && !keepsEnglish ? "" : undefined}
                    className={l.dim ? `${linkClass} opacity-50` : linkClass}
                  >
                    {l.label[locale]}
                  </LocalLink>
                );
              })}
            </nav>
          </div>
        ))}
      </div>

      {/* NAP Block */}
      <div className="border-t border-primary-foreground/10 mt-20 pt-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
          <div className="flex items-stretch gap-8">
            <img
              src={logoWhite}
              alt="Event Boutique – Luxury Wedding Atelier in Portugal"
              className="h-auto w-auto max-h-[5.5rem] self-center"
              width={220}
              height={88}
              loading="lazy"
              decoding="async"
            />
            <address className="not-italic leading-relaxed uppercase">
              <span className="text-[14px] opacity-80">Btq - Botique de Eventos, Lda</span><br />
              <span className="text-[14px] opacity-80">2750-005 Cascais, Portugal</span><br />
              <span className="text-[13px] opacity-70">NIF: 517199041</span><br />
              {/* Административный номер — намеренно здесь и только здесь
                  (свод решений 23.08). Клиентский номер 923 272 822 (с 30.10.2026, TK-834;
                  прежний 936 540 344 отключён) стоит во всём
                  публичном: шапка, контакты, WhatsApp, telephone в JSON-LD. Этот
                  в разметку не попадает, чтобы сущность склеивалась с карточкой
                  Google Maps по одному номеру. */}
              <a href="tel:+351934488982" className="text-[13px] opacity-70 hover:opacity-100 transition-opacity mt-1 inline-block">
                +351 934 488 982 (Administrative)
              </a>
            </address>
          </div>
          <div className="flex flex-col items-start md:items-end gap-3">
            <div className="flex gap-4">
              <a href="https://www.instagram.com/event_boutique_portugal" target="_blank" rel="noopener noreferrer" className="text-[13px] opacity-70 hover:opacity-100 transition-opacity" aria-label="Instagram">
                Instagram
              </a>
              <a href="https://pt.pinterest.com/boutiqueventportugal/" target="_blank" rel="noopener noreferrer" className="text-[13px] opacity-70 hover:opacity-100 transition-opacity" aria-label="Pinterest">
                Pinterest
              </a>
              <a href="https://t.me/event_boutique_pt" target="_blank" rel="noopener noreferrer" className="text-[13px] opacity-70 hover:opacity-100 transition-opacity" aria-label="Telegram">
                Telegram
              </a>
              <a href="https://wa.me/351923272822" target="_blank" rel="noopener noreferrer" className="text-[13px] opacity-70 hover:opacity-100 transition-opacity" aria-label="WhatsApp">
                WhatsApp
              </a>
            </div>
            {/* Английская ветка — буквально прежний JSX. Подстановка локали рядом
                с {getFullYear()} меняет число текстовых узлов, а значит и
                разметку английской страницы: React ставит между соседними
                узлами разделитель <!-- -->. Проверка идентичности английской
                стороны (main против ветки) на этом и ловится. */}
            {locale === "pt" ? (
              <p className="text-[13px] opacity-70">{`© ${new Date().getFullYear()} Event Boutique. ${RIGHTS.pt}`}</p>
            ) : (
              <p className="text-[13px] opacity-70">© {new Date().getFullYear()} Event Boutique. All rights reserved.</p>
            )}
          </div>
        </div>
      </div>
      </div>
    </footer>
  );
};

export default Footer;